import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Lock, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { FEATURES, nextPlanFor } from '@/lib/entitlements';

// Calm overlay shown when a Clarity user lands on a Horizon-only page.
export function UpgradeGate({ featureKey, children }) {
  const f = FEATURES[featureKey];
  if (!f) return children;
  const tier = (f.minTier || '').replace(/^./, (c) => c.toUpperCase());
  return (
    <div className="max-w-2xl mx-auto py-10">
      <Card className="rounded-3xl border-[hsl(var(--gold))]/30 bg-card shadow-brand-lg overflow-hidden">
        <CardContent className="p-8 sm:p-10 text-center space-y-4">
          <div className="inline-flex items-center gap-2 text-[11px] px-3 py-1 rounded-full bg-[hsl(var(--gold))]/15 text-[hsl(var(--gold))] border border-[hsl(var(--gold))]/30">
            <Sparkles className="h-3.5 w-3.5" /> Available in {tier}
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">{f.label}</h2>
          <p className="text-sm text-muted-foreground leading-relaxed max-w-md mx-auto">{f.upsell || 'A deeper layer of financial clarity, available when you are ready.'}</p>
          <div className="flex items-center justify-center gap-2 pt-2">
            <Link to="/pricing"><Button className="rounded-xl" data-testid="upgrade-view-plans">View plans <ArrowRight className="h-4 w-4 ml-1" /></Button></Link>
            <Link to="/dashboard"><Button variant="ghost" className="rounded-xl">Back to dashboard</Button></Link>
          </div>
          <div className="text-[11px] text-muted-foreground pt-2">No rush. No pressure. Move up when it feels right.</div>
        </CardContent>
      </Card>
    </div>
  );
}

// Inline pill used on cards/sections.
export function UpsellPill({ featureKey, className = '' }) {
  const tier = nextPlanFor(featureKey);
  if (!tier) return null;
  const label = tier.replace(/^./, (c) => c.toUpperCase());
  return (
    <Link to="/pricing" className={`inline-flex items-center gap-1.5 text-[10px] rounded-full px-2 py-0.5 bg-[hsl(var(--gold))]/12 text-[hsl(var(--gold))] border border-[hsl(var(--gold))]/30 hover:bg-[hsl(var(--gold))]/20 transition ${className}`}>
      <Sparkles className="h-3 w-3" /> Available in {label}
    </Link>
  );
}

export function LockOverlay({ featureKey }) {
  const tier = nextPlanFor(featureKey);
  const label = (tier || '').replace(/^./, (c) => c.toUpperCase());
  return (
    <div className="absolute inset-0 grid place-items-center bg-card/70 backdrop-blur-[2px] rounded-2xl">
      <div className="text-center px-4">
        <Lock className="h-4 w-4 text-[hsl(var(--gold))] mx-auto" />
        <div className="text-xs text-muted-foreground mt-1">Available in {label}</div>
        <Link to="/pricing" className="text-xs text-primary underline">View plans</Link>
      </div>
    </div>
  );
}
